"use client"

import Image from "next/image"
import Link from "next/link"
import { useState, type FormEvent } from "react"
import { toast } from "sonner"
import { sendContactEmail } from "@/app/actions/contact"
import { trackContactLead } from "@/lib/analytics"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const buttonBase =
  "inline-flex items-center justify-center rounded-[2px] font-medium text-[clamp(12px,1.16vw,22px)] leading-[clamp(14px,1.4vw,26px)] h-[clamp(30px,2.9vw,56px)] px-[clamp(18px,1.7vw,32px)] touch-manipulation transition-opacity duration-[200ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] hover:opacity-90 motion-reduce:transition-none motion-reduce:hover:opacity-100 focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[2px] cursor-pointer"

const inputBase =
  "w-full border-b bg-transparent text-[clamp(13px,1.1vw,16px)] leading-[1.4] text-[color:var(--color-black)] placeholder:text-[color:var(--color-black)] placeholder:opacity-60 focus:outline-none transition-colors duration-200 pb-[clamp(8px,0.8vw,14px)]"

// El campo desplegable "unidades" se renderiza aparte, entre Empresa y Telefono.
const fieldsBeforeUnidades = [
  { name: "nombre", label: "Nombre Completo", type: "text", required: false },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "empresa", label: "Empresa", type: "text", required: true },
] as const

const fieldsAfterUnidades = [
  { name: "telefono", label: "Telefono", type: "tel", required: false },
] as const

const unidadesOptions = [
  "Menos de 50 unidades",
  "50 a 200 unidades",
  "200 a 500 unidades",
  "Más de 500 unidades",
] as const

type ErrorKey = "email" | "empresa" | "unidades" | "mensaje"
type FieldErrors = Partial<Record<ErrorKey, boolean>>

function FieldError({ message = "Este campo es obligatorio" }: { message?: string }) {
  return (
    <p className="mt-[6px] flex items-center gap-[5px] text-[clamp(10px,0.8vw,12px)] font-medium leading-[1.3] text-[color:var(--color-red)]">
      <span aria-hidden="true" className="inline-block h-[4px] w-[4px] shrink-0 rounded-full bg-[color:var(--color-red)]" />
      {message}
    </p>
  )
}

export default function Contact() {
  const [isPending, setIsPending] = useState(false)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [emailErrorMsg, setEmailErrorMsg] = useState<string | null>(null)
  const [unidades, setUnidades] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)

  function clearError(field: ErrorKey) {
    setErrors((prev) => ({ ...prev, [field]: false }))
    if (field === "email") setEmailErrorMsg(null)
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const formData = new FormData(form)

    const emailValue = String(formData.get("email") ?? "").trim()
    const unidadesValue = String(formData.get("unidades") ?? "").trim()
    const emailInvalidFormat = Boolean(emailValue) && !EMAIL_RE.test(emailValue)

    const newErrors: FieldErrors = {
      email: !emailValue || emailInvalidFormat,
      empresa: !String(formData.get("empresa") ?? "").trim(),
      unidades: !unidadesValue,
      mensaje: !String(formData.get("mensaje") ?? "").trim(),
    }
    setEmailErrorMsg(emailInvalidFormat ? "Ingresá un email válido" : null)
    setErrors(newErrors)
    if (Object.values(newErrors).some(Boolean)) return

    setIsPending(true)
    try {
      const result = await sendContactEmail({ status: "idle" }, formData)
      if (result.status === "success") {
        trackContactLead(unidadesValue)
        toast.success(result.message)
        form.reset()
        setErrors({})
        setEmailErrorMsg(null)
        setUnidades("")
        setIsSuccess(true)
      } else {
        toast.error(result.message)
      }
    } catch {
      toast.error(
        "Hubo un error al enviar. Intentá de nuevo o escribinos a hola@vacavaliente.com.",
      )
    } finally {
      setIsPending(false)
    }
  }

  return (
    <section
      id="contact"
      className="bg-[color:var(--color-white)] lg:pt-[clamp(56px,7vw,120px)] scroll-mt-[88px] md:scroll-mt-[104px]"
    >
      <div className="px-[clamp(16px,2.7vw,52px)] lg:pr-0 pb-20 lg:pb-0">
        <div className="flex flex-col gap-[clamp(32px,4vw,80px)] lg:flex-row lg:items-start">
          <div className="w-full lg:w-1/2">
            <h2 className="text-[clamp(28px,4.2vw,64px)] leading-[1.1] font-[800] text-[color:var(--color-black)]">
              Contacto
              <span className="text-[color:var(--color-red)]">.</span>
            </h2>

            {isSuccess ? (
              <div
                role="status"
                aria-live="polite"
                className="mt-[clamp(26px,3vw,60px)] flex flex-col items-start gap-[clamp(14px,1.6vw,24px)] rounded-[2px] border border-[color:var(--color-divider)] p-[clamp(24px,2.6vw,40px)]"
              >
                <span className="inline-flex h-[clamp(40px,3.4vw,56px)] w-[clamp(40px,3.4vw,56px)] items-center justify-center rounded-full bg-[color:var(--color-red)]">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className="h-[clamp(20px,1.7vw,28px)] w-[clamp(20px,1.7vw,28px)] text-[color:var(--color-white)]"
                  >
                    <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div className="flex flex-col gap-[6px]">
                  <h3 className="text-[clamp(20px,2vw,32px)] leading-[1.15] font-[800] text-[color:var(--color-black)]">
                    ¡Gracias! Recibimos tu consulta
                    <span className="text-[color:var(--color-red)]">.</span>
                  </h3>
                  <p className="text-[clamp(13px,1.1vw,16px)] leading-[1.5] text-[color:var(--color-black)] opacity-70">
                    Un asesor del equipo te va a contactar a la brevedad. Mientras
                    tanto, podés ver nuestro catálogo.
                  </p>
                </div>
                <div className="mt-[6px] flex items-center gap-[12px]">
                  <Link
                    href="/docs/Catalogo-2026.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className={`${buttonBase} bg-[color:var(--color-red)] text-[color:var(--color-white)] focus-visible:outline-[color:var(--color-black)]`}
                  >
                    ver catálogo
                  </Link>
                  <button
                    type="button"
                    onClick={() => setIsSuccess(false)}
                    className={`${buttonBase} border border-[color:var(--color-black)] text-[color:var(--color-black)] focus-visible:outline-[color:var(--color-black)]`}
                  >
                    enviar otra consulta
                  </button>
                </div>
              </div>
            ) : (
            <form
              onSubmit={handleSubmit}
              noValidate
              className="mt-[clamp(26px,3vw,60px)] flex flex-col gap-[clamp(20px,2.2vw,36px)]"
            >
              {fieldsBeforeUnidades.map((field) => {
                const hasError = field.required && errors[field.name as ErrorKey]
                return (
                  <div key={field.name} className="flex flex-col">
                    <label
                      htmlFor={`contact-${field.name}`}
                      className="mb-[6px] text-[clamp(11px,0.9vw,14px)] leading-[1.3] font-medium text-[color:var(--color-black)]"
                    >
                      {field.label}
                      {field.required && (
                        <span aria-hidden="true" className="text-[color:var(--color-red)]">*</span>
                      )}
                      {field.required && <span className="sr-only"> (requerido)</span>}
                    </label>
                    <input
                      id={`contact-${field.name}`}
                      name={field.name}
                      type={field.type}
                      aria-invalid={hasError ? "true" : undefined}
                      onChange={() => { if (field.required) clearError(field.name as ErrorKey) }}
                      className={`${inputBase} ${hasError ? "border-b-[color:var(--color-red)]" : "border-b-[color:var(--color-divider)] focus:border-b-[color:var(--color-black)]"}`}
                    />
                    {hasError && (
                      <FieldError
                        message={field.name === "email" && emailErrorMsg ? emailErrorMsg : undefined}
                      />
                    )}
                  </div>
                )
              })}

              <div className="flex flex-col">
                <label
                  htmlFor="contact-unidades"
                  className="mb-[6px] text-[clamp(11px,0.9vw,14px)] leading-[1.3] font-medium text-[color:var(--color-black)]"
                >
                  ¿Cuántas unidades necesitás?
                  <span aria-hidden="true" className="text-[color:var(--color-red)]">*</span>
                  <span className="sr-only"> (requerido)</span>
                </label>
                <div className="relative">
                  <select
                    id="contact-unidades"
                    name="unidades"
                    value={unidades}
                    aria-invalid={errors.unidades ? "true" : undefined}
                    onChange={(e) => { setUnidades(e.target.value); clearError("unidades") }}
                    className={`${inputBase} appearance-none cursor-pointer pr-[28px] ${unidades ? "" : "opacity-60"} ${errors.unidades ? "border-b-[color:var(--color-red)]" : "border-b-[color:var(--color-divider)] focus:border-b-[color:var(--color-black)]"}`}
                  >
                    <option value="" disabled>
                      Seleccioná una opción
                    </option>
                    {unidadesOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 12 8"
                    fill="none"
                    className="pointer-events-none absolute right-0 top-[0.7em] h-[7px] w-[11px] -translate-y-1/2 text-[color:var(--color-black)]"
                  >
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {errors.unidades && <FieldError />}
              </div>

              {fieldsAfterUnidades.map((field) => {
                const hasError = field.required && errors[field.name as ErrorKey]
                return (
                  <div key={field.name} className="flex flex-col">
                    <label
                      htmlFor={`contact-${field.name}`}
                      className="mb-[6px] text-[clamp(11px,0.9vw,14px)] leading-[1.3] font-medium text-[color:var(--color-black)]"
                    >
                      {field.label}
                      {field.required && (
                        <span aria-hidden="true" className="text-[color:var(--color-red)]">*</span>
                      )}
                      {field.required && <span className="sr-only"> (requerido)</span>}
                    </label>
                    <input
                      id={`contact-${field.name}`}
                      name={field.name}
                      type={field.type}
                      aria-invalid={hasError ? "true" : undefined}
                      onChange={() => { if (field.required) clearError(field.name as ErrorKey) }}
                      className={`${inputBase} ${hasError ? "border-b-[color:var(--color-red)]" : "border-b-[color:var(--color-divider)] focus:border-b-[color:var(--color-black)]"}`}
                    />
                    {hasError && <FieldError />}
                  </div>
                )
              })}

              <div className="flex flex-col">
                <label
                  htmlFor="contact-mensaje"
                  className="mb-[6px] text-[clamp(11px,0.9vw,14px)] leading-[1.3] font-medium text-[color:var(--color-black)]"
                >
                  Dejá acá tu consulta
                  <span aria-hidden="true" className="text-[color:var(--color-red)]">*</span>
                  <span className="sr-only"> (requerido)</span>
                </label>
                <textarea
                  id="contact-mensaje"
                  name="mensaje"
                  rows={1}
                  aria-invalid={errors.mensaje ? "true" : undefined}
                  onChange={() => clearError("mensaje")}
                  className={`${inputBase} resize-none ${errors.mensaje ? "border-b-[color:var(--color-red)]" : "border-b-[color:var(--color-divider)] focus:border-b-[color:var(--color-black)]"}`}
                />
                {errors.mensaje && <FieldError />}
              </div>

              <p className="mt-[clamp(4px,0.6vw,10px)] text-[clamp(11px,0.9vw,14px)] leading-[1.4] text-[color:var(--color-black)] opacity-70">
                Trabajamos con empresas desde 50 unidades.
              </p>

              <div className="mt-[clamp(8px,1vw,16px)] flex items-center gap-[12px]">
                <Link
                  href="/docs/Catalogo-2026.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className={`${buttonBase} border border-[color:var(--color-black)] text-[color:var(--color-black)] focus-visible:outline-[color:var(--color-black)]`}
                >
                  ver catalogo
                </Link>
                <button
                  type="submit"
                  disabled={isPending}
                  className={`${buttonBase} bg-[color:var(--color-red)] text-[color:var(--color-white)] focus-visible:outline-[color:var(--color-black)] disabled:opacity-60`}
                >
                  {isPending ? "enviando..." : "contactate"}
                </button>
              </div>
            </form>
            )}
          </div>

          <div className="relative w-full lg:w-1/2 hidden lg:block">
            <div className="relative aspect-square w-full h-full overflow-hidden rounded-[2px]">
              <Image
                src="/images/contact/Contact.jpeg"
                alt="Productos personalizados Vacavaliente: cuadernos, fundas y accesorios de cuero"
                fill
                className="object-cover w-full self-end"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
